import { Badge, Button, Pagination, Stack, Table, Text } from "@bookingcom/bui-react";
import { EditIcon } from "@bookingcom/bui-assets-react/streamline";
import type { PortalUser } from "../../types";
import { UserManagementEmptyState } from "./UserManagementEmptyState";

type UserManagementTableProps = {
  users: PortalUser[];
  page: number;
  totalCount: number;
  size: number;
  onPageChange: (page: number) => void;
  onEdit?: (user: PortalUser) => void;
};

export const UserManagementTable = ({
  users,
  page,
  totalCount,
  size,
  onPageChange,
  onEdit,
}: UserManagementTableProps) => {
  const totalPages = Math.ceil(totalCount / size);

  if (users.length === 0) {
    return <UserManagementEmptyState />;
  }

  return (
    <Stack gap={0}>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Heading>USER</Table.Heading>
            <Table.Heading>ACCESS</Table.Heading>
            <Table.Heading width="120px" />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {users.map((user) => (
            <Table.Row key={user.auth0UserId}>
              <Table.Cell>
                <Stack gap={0}>
                  <Text variant="emphasized_1">{user.name}</Text>
                  <Text variant="body_2" color="neutral_alt">
                    {user.email}
                  </Text>
                </Stack>
              </Table.Cell>
              <Table.Cell>
                {!user.groupName && user.airlineCodes.length === 0 ? (
                  <Text variant="body_2" color="neutral_alt">-</Text>
                ) : (
                  <Stack gap={1}>
                    {user.groupName && <Text variant="body_2">{user.groupName}</Text>}
                    <Stack direction="row" gap={1}>
                      {user.airlineCodes.map((code) => (
                        <Badge key={code} variant="outline" text={code} />
                      ))}
                    </Stack>
                  </Stack>
                )}
              </Table.Cell>
              <Table.Cell align="center">
                <Button
                  variant="tertiary"
                  icon={EditIcon}
                  size="medium"
                  attributes={{ "aria-label": `Edit ${user.name}` }}
                  onClick={() => onEdit?.(user)}
                />
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {totalPages > 1 && (
        <Pagination
          currentPage={page + 1}
          totalPages={totalPages}
          ariaPageLabelPrefix="Page"
          ariaPreviousLabel="Previous page"
          ariaNextLabel="Next page"
          onPageChange={({ page: newPage }) => onPageChange(newPage - 1)}
        />
      )}
    </Stack>
  );
};
